/**
 * crossrefService.ts - Integração com API CrossRef
 * Bigwa v1.0 - Janeiro 2026
 * 
 * API Docs: https://api.crossref.org/swagger-ui/index.html
 * Rate limit: 50 req/seg com email (polite pool)
 */

import {
  WorkCandidate,
  Author,
  mapCrossRefType,
  normalizeWorkKey
} from '../../types/capturaInteligente';
import { logger } from '../../utils/logger';

const CROSSREF_BASE_URL = 'https://api.crossref.org/works';
const CROSSREF_EMAIL = 'contato@bigwa.app'; // Para polite pool

interface CrossRefAuthor {
  given?: string;
  family?: string;
  name?: string;
}

interface CrossRefItem {
  DOI?: string;
  title?: string[];
  author?: CrossRefAuthor[];
  published?: { 'date-parts'?: number[][] };
  'published-print'?: { 'date-parts'?: number[][] };
  'published-online'?: { 'date-parts'?: number[][] };
  type?: string;
  'container-title'?: string[];
  publisher?: string;
  volume?: string;
  issue?: string;
  page?: string;
  abstract?: string;
  URL?: string;
  'is-referenced-by-count'?: number;
}

function parseAuthors(crossRefAuthors: CrossRefAuthor[] | undefined): Author[] {
  if (!crossRefAuthors || crossRefAuthors.length === 0) {
    return [{ firstName: '', lastName: 'Desconhecido' }];
  }
  
  return crossRefAuthors.map(a => ({
    firstName: a.given || '',
    lastName: a.family || a.name || 'Desconhecido',
  }));
}

function parseYear(item: CrossRefItem): number {
  const dateParts = 
    item.published?.['date-parts']?.[0] ||
    item['published-print']?.['date-parts']?.[0] ||
    item['published-online']?.['date-parts']?.[0];
  
  return dateParts?.[0] || new Date().getFullYear();
}

function crossRefItemToCandidate(item: CrossRefItem, relevanceScore = 50, relevanceReason = ''): WorkCandidate {
  const authors = parseAuthors(item.author);
  const year = parseYear(item);
  const title = item.title?.[0] || 'Sem título';
  
  return {
    id: item.DOI || `crossref-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    title,
    authors,
    year,
    type: mapCrossRefType(item.type || 'article'),
    doi: item.DOI,
    journal: item['container-title']?.[0],
    publisher: item.publisher,
    volume: item.volume,
    issue: item.issue,
    pages: item.page,
    abstract: item.abstract?.replace(/<[^>]*>/g, ''), // Remove HTML tags
    url: item.URL,
    relevanceScore,
    relevanceReason,
    source: 'crossref',
    normalizedKey: normalizeWorkKey(
      authors.map(a => a.lastName),
      year,
      title
    ),
    citationCount: item['is-referenced-by-count'],
  };
}

/**
 * Busca principal por autor, ano e título
 */
export async function searchByAuthorYearTitle(
  author: string,
  year: number | null,
  title: string
): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'query.bibliographic': `${author} ${title}`.trim(),
      'rows': '10',
      'sort': 'relevance',
      'mailto': CROSSREF_EMAIL,
    });
    
    if (year) {
      params.append('filter', `from-pub-date:${year - 1},until-pub-date:${year + 1}`);
    }
    
    const response = await fetch(`${CROSSREF_BASE_URL}?${params}`);
    
    if (!response.ok) {
      logger.error('CrossrefService', 'Error:', response.status);
      return [];
    }
    
    const data = await response.json();
    const items: CrossRefItem[] = data.message?.items || [];
    
    return items.map((item, index) => {
      // Calcular relevância baseada na posição e match
      const positionScore = Math.max(0, 100 - (index * 10));
      const yearMatch = year && parseYear(item) === year ? 20 : 0;
      const relevance = Math.min(100, positionScore + yearMatch);
      
      return crossRefItemToCandidate(
        item, 
        relevance,
        index === 0 ? 'Melhor correspondência' : `Resultado #${index + 1}`
      );
    });
  } catch (error) {
    logger.error('CrossrefService', 'Search error:', error);
    return [];
  }
}

/**
 * Busca por DOI específico
 */
export async function searchByDOI(doi: string): Promise<WorkCandidate | null> {
  try {
    const cleanDoi = doi.replace(/^https?:\/\/doi\.org\//, '');
    const response = await fetch(`${CROSSREF_BASE_URL}/${encodeURIComponent(cleanDoi)}?mailto=${CROSSREF_EMAIL}`);
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    const item: CrossRefItem = data.message;
    
    if (!item) return null;
    
    return crossRefItemToCandidate(item, 100, 'DOI exato');
  } catch (error) {
    logger.error('CrossrefService', 'DOI search error:', error);
    return null;
  }
}

/**
 * Busca outras obras do mesmo autor
 */
export async function searchByAuthor(
  authorLastName: string,
  excludeYear?: number,
  limit = 10
): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'query.author': authorLastName,
      'rows': limit.toString(),
      'sort': 'published',
      'order': 'desc',
      'mailto': CROSSREF_EMAIL,
    });
    
    const response = await fetch(`${CROSSREF_BASE_URL}?${params}`);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    const items: CrossRefItem[] = data.message?.items || [];
    
    return items
      .filter(item => !excludeYear || parseYear(item) !== excludeYear)
      .map(item => crossRefItemToCandidate(
        item,
        70,
        `Outra obra de ${authorLastName}`
      ));
  } catch (error) {
    logger.error('CrossrefService', 'Author search error:', error);
    return [];
  }
}

/**
 * Busca por termo genérico (título parcial, palavras-chave)
 */
export async function searchGeneric(
  query: string,
  limit = 15
): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'query': query,
      'rows': limit.toString(),
      'sort': 'relevance',
      'mailto': CROSSREF_EMAIL,
    });
    
    const response = await fetch(`${CROSSREF_BASE_URL}?${params}`);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    const items: CrossRefItem[] = data.message?.items || [];
    
    return items.map((item, index) => crossRefItemToCandidate(
      item,
      Math.max(30, 80 - (index * 5)),
      'Termo relacionado'
    ));
  } catch (error) {
    logger.error('CrossrefService', 'Generic search error:', error);
    return [];
  }
}

export const crossrefService = {
  searchByAuthorYearTitle,
  searchByDOI,
  searchByAuthor,
  searchGeneric,
};
